import { formatMeridianTime } from '@airship-platform/airship-patterns';
import { FlexBox, SectionLoader } from '@airship-platform/jam-web/components';
import Timeline from '@mui/lab/Timeline';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import { Typography } from '@mui/material';
import Box from '@mui/material/Box';
import { AppointmentProposalOption } from '../../../../data-access/appointmentProposalOptions';
import { MergedJobActivity } from '../../../../data-access/jobsActivitiesTracking';

type ActivitySectionProps = {
  isLoading: boolean;
  isLoaded: boolean;
  options: AppointmentProposalOption[];
  activitiesGroupedByDate: Record<string, MergedJobActivity[]>;
};

const ActivitySection: React.FC<ActivitySectionProps> = ({
  isLoading,
  isLoaded,
  options,
  activitiesGroupedByDate,
}) => {
  const getActivityMessage = (activity: MergedJobActivity): React.ReactNode => {
    const getUserDisplayName = (
      details?: MergedJobActivity['details']
    ): string | null => {
      if (!details || !('lastName' in details) || !('firstName' in details))
        return null;
      const lastInitial = details.lastName?.charAt(0);
      return `${details.firstName} ${
        lastInitial ? `${lastInitial}.` : ''
      }`.trim();
    };

    const optionCountPhrase = (count: number): string =>
      count === 1 ? 'an option' : `${count} options`;

    const timesPhrase = (count: number): string =>
      count > 1 ? `${count} times` : '';

    const name = getUserDisplayName(activity.details);

    switch (activity.activityType) {
      case 'APPOINTMENT_PROPOSAL_CREATED':
        return name ? (
          <Typography variant="body1">
            <Typography component="span" sx={{ fontWeight: 700 }}>
              {name}
            </Typography>{' '}
            created the proposal
          </Typography>
        ) : (
          <Typography variant="body1">The proposal was created</Typography>
        );

      case 'APPOINTMENT_PROPOSAL_OPTION_CREATED': {
        const phrase = optionCountPhrase(activity.count);
        return name ? (
          <Typography variant="body1">
            <Typography component="span" sx={{ fontWeight: 700 }}>
              {name}
            </Typography>{' '}
            created {phrase}
          </Typography>
        ) : (
          <Typography variant="body1">
            {activity.count === 1
              ? 'An option was created'
              : `${activity.count} options were created`}
          </Typography>
        );
      }

      case 'APPOINTMENT_PROPOSAL_OPTION_TECHNICIAN_ACCEPTED': {
        return name ? (
          <Typography variant="body1">
            Proposal option{' '}
            <Typography component="span" sx={{ fontWeight: 700 }}>
              {activity.details?.optionName}
            </Typography>{' '}
            was accepted by{' '}
            <Typography component="span" sx={{ fontWeight: 700 }}>
              {name}
            </Typography>{' '}
          </Typography>
        ) : (
          <Typography variant="body1">
            Proposal option{' '}
            <Typography component="span" sx={{ fontWeight: 700 }}>
              {activity.details?.optionName}
            </Typography>{' '}
            was accepted
          </Typography>
        );
      }

      case 'APPOINTMENT_PROPOSAL_OPTION_CUSTOMER_ACCEPTED': {
        return (
          <Typography variant="body1">
            Proposal option{' '}
            <Typography component="span" sx={{ fontWeight: 700 }}>
              {activity.details?.optionName}
            </Typography>{' '}
            was accepted by the customer
          </Typography>
        );
      }

      case 'APPOINTMENT_PROPOSAL_FOLLOW_UP_SENT': {
        return name ? (
          <Typography variant="body1">
            <Typography component="span" sx={{ fontWeight: 700 }}>
              {name}
            </Typography>{' '}
            sent a follow-up email to the customer
          </Typography>
        ) : (
          <Typography variant="body1">
            A follow-up email was sent to the customer
          </Typography>
        );
      }

      case 'APPOINTMENT_PROPOSAL_SHARED': {
        const times = timesPhrase(activity.count);
        return name ? (
          <Typography variant="body1">
            <Typography component="span" sx={{ fontWeight: 700 }}>
              {name}
            </Typography>{' '}
            shared the proposal presentation {times}
          </Typography>
        ) : (
          <Typography variant="body1">
            The proposal presentation was shared {times}
          </Typography>
        );
      }

      default:
        return null;
    }
  };

  if (isLoading && !isLoaded) {
    return <SectionLoader isLoading title={'Fetching activities...'} />;
  }

  return (
    <Box sx={{ width: '100%' }}>
      {Object.keys(activitiesGroupedByDate).map((date) => (
        <FlexBox key={date} flexDirection={'column'} gap={2}>
          <Typography variant="h4">{date}</Typography>
          <Timeline>
            {activitiesGroupedByDate[date].map((a: MergedJobActivity) => (
              <TimelineItem key={a.activityId}>
                <TimelineSeparator>
                  <TimelineDot />
                  <TimelineConnector />
                </TimelineSeparator>
                <TimelineContent>
                  <FlexBox gap={1} justifyContent={'space-between'}>
                    {getActivityMessage(a)}
                    <Typography variant="body2">
                      {formatMeridianTime(a.createdAt)}
                    </Typography>
                  </FlexBox>
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
        </FlexBox>
      ))}
    </Box>
  );
};

export default ActivitySection;
